import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X } from "lucide-react";
import { type Lang, type Translations } from "@/lib/i18n";
import { BrandName } from "./BrandName";

// CTA path-with-lang: /zh/ visitors clicking the gradient button arrive at
// the login page with ?lang=zh so detectLang picks up Chinese instead of
// falling back to navigator (many Chinese SEO visitors have non-zh-CN
// browser locale).
const appUrl = (lang: Lang): string =>
  lang === "zh" ? "/app/login?lang=zh" : "/app/login";

export const Navbar = ({ setActiveSection, lang, setLang, tr }: { setActiveSection: (s: string) => void; lang: Lang; setLang: (l: Lang) => void; tr: Translations }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: tr.nav.services, id: "services" },
    { name: tr.nav.gallery, id: "gallery" },
    { name: tr.nav.pricing, id: "pricing" },
    { name: tr.nav.contact, id: "contact" }
  ];

  return (
    <>
    <nav style={{ top: 'var(--promo-h, 0px)' }} className={`fixed left-0 w-full z-50 transition-[background-color,backdrop-filter,border-color,border-width,padding] duration-300 ${isScrolled ? "bg-white/80 backdrop-blur-md border-b border-black/5 py-4" : "bg-transparent py-6"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setActiveSection("hero")}>
          <div className="flex items-center gap-2.5">
            <img src="/logo.webp" alt="POPWIZ" className="w-9 h-9 object-contain" />
            <BrandName lang={lang} />
          </div>
        </div>
        <div className="hidden lg:flex items-center space-x-12">
          {navItems.map((item) => (
            <button key={item.id} onClick={() => setActiveSection(item.id)} className="label-vogue text-black/50 hover:text-black transition-colors cursor-pointer">
              {item.name}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => setLang(lang === 'en' ? 'zh' : 'en')} className="hidden lg:block label-vogue text-black/50 hover:text-black transition-colors cursor-pointer">
            {lang === 'en' ? '中文' : 'EN'}
          </button>
          <a href={appUrl(lang)} className="hidden lg:block btn-gradient px-6 py-2.5 label-vogue hover:opacity-90 transition-opacity cursor-pointer no-underline">
            {tr.nav.tryFree}
          </a>
          <button className="lg:hidden p-2" aria-label={lang === 'zh' ? '菜单' : 'Menu'} aria-expanded={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {/* Dropdown stays INSIDE the nav: it's absolute (top-full), meant to be nav-relative
          and render directly below the bar. */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-black/5 p-6 flex flex-col space-y-6 shadow-xl z-50">
            {navItems.map((item) => (
              <button key={item.id} onClick={() => { setActiveSection(item.id); setIsMobileMenuOpen(false); }} className="label-vogue text-left">
                {item.name}
              </button>
            ))}
            <button onClick={() => setLang(lang === 'en' ? 'zh' : 'en')} className="label-vogue text-left cursor-pointer">{lang === 'en' ? '中文' : 'EN'}</button>
            <a href={appUrl(lang)} className="btn-gradient px-6 py-3 label-vogue w-full cursor-pointer block text-center no-underline">{tr.nav.tryFree}</a>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
    {/* Backdrop rendered OUTSIDE the <nav>: when scrolled, the nav's `backdrop-filter`
        makes it the containing block for `fixed` descendants, so a backdrop inside would
        be nav-relative (offset by --promo-h, sized to the nav box) and miss the promo
        strip. As a top-level sibling it stays viewport-fixed and covers the whole screen,
        incl. the banner (z-40 > banner z-[39]). */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="lg:hidden fixed inset-0 bg-black/20 z-40" onClick={() => setIsMobileMenuOpen(false)} />
        )}
      </AnimatePresence>
    </>
  );
};
